import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useSWR from 'swr';

import axios from '../../admin-axios';
import { detectError } from '../../utils';
import LongPaginationHandler from '../LongPaginationHandler';
import SearchAndFilter from '../SearchAndFilter';
import classes from './index.module.scss';

const fetcher = (url) => axios.get(url).then((res) => res.data);

const PumpTable = () => {
  const { pumpId } = useParams();
  const tableDate = useRef('2022-11-17');
  const [searchLoading, setSearchLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchPumps, setSearchPumps] = useState([]);
  const [searchError, setSearchError] = useState(null);
  const [view, setView] = useState('normal');
  const [filterResult, setFilterResult] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterPage, setFilterPage] = useState(1);
  const [filterTotal, setFilterTotal] = useState(0);
  const [filterPerPage, setFilterPerPage] = useState(20);
  const [filterError, setFilterError] = useState('');
  const searchRef = useRef();
  const dateRef = useRef();

  const {
    user: { stationId }
  } = useSelector((state) => state.auth);

  const {
    data,
    error: fetchError,
    isLoading
  } = useSWR(`/pump/transactions/${pumpId}/${stationId}?page=${page}`, fetcher);
  // } = useSWR(`/pump/transactions/${pumpId}`, fetcher);

  const searchHandler = async (event) => {
    if (event.code === 'Enter') {
      if (!searchRef.current.value) {
        toast.error('Please enter a search query');
        return;
      }
      setView('searching');
      try {
        setSearchLoading(true);
        setSearchPumps([]);
        setSearchError('');
        const result = await axios.get(
          `/pump/transaction/search/${searchRef.current.value}/${pumpId}/${stationId}`
        );
        setSearchPumps(result.data.message);
        setSearchLoading(false);
      } catch (e) {
        setSearchError(detectError(e));
        setSearchLoading(false);
      }
      searchRef.current.value = '';
    }
  };

  const paginate = (pageNumber) => {
    setPage(pageNumber);
  };

  const paginateFilter = (pageNumber) => {
    setFilterPage(pageNumber);
  };

  const filterByDateHandler = async () => {
    if (!dateRef.current.value) {
      toast.error('Please choose a date to filter by');
      return;
    }
    const toSend = {
      date: dateRef.current.value
    };

    try {
      setFilterLoading(true);
      setView('filtering');
      const res = await axios.post(`/pump/transaction/filter/${pumpId}/${stationId}`, toSend);
      setFilterResult(res.data.message.data);
      setFilterPage(res.data.message.current_page);
      setFilterTotal(res.data.message.total);
      setFilterPerPage(res.data.message.per_page);
      setFilterLoading(false);
    } catch (e) {
      setFilterError(detectError(e));
      setFilterLoading(false);
    }
  };

  useEffect(() => {
    if (filterPage && dateRef.current?.value) {
      filterByDateHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPage]);

  useEffect(() => {
    document.body.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <>
      {isLoading && (
        <div className="w-100 h-100 d-flex justify-content-center align-items-center">
          <Spinner animation="border" variant="primary" />
        </div>
      )}
      {fetchError && (
        <div className="w-100 h-100 d-flex justify-content-center align-items-center">
          <h3 className="text-danger">{detectError(fetchError)}</h3>
        </div>
      )}
      {!isLoading && !fetchError && data && (
        <div className="w-100 h-100">
          <div className="row mb-3">
            <div className="col-12 col-md-5">
              <div className="d-flex">
                <div className={`d-flex ${classes.pump_div}`}>
                  <span className={classes.pump_id}>Pump ID</span>
                  <h2>{data.pump_id}</h2>
                </div>
                <span className="mt-3 ms-3">{data.status}</span>
                {data.status === 'Active' ? (
                  <span className={classes.pump_active}>&nbsp;</span>
                ) : (
                  <span className={classes.pump_inactive}>&nbsp;</span>
                )}
              </div>
            </div>
          </div>
          <SearchAndFilter
            searchRef={searchRef}
            dateRef={dateRef}
            searchHandler={searchHandler}
            searchText="CIN, Customer Name, Phone No."
            filterByDateHandler={filterByDateHandler}
          />
          {(view === 'searching' || view === 'filtering') && (
            <div className="">
              <button
                onClick={() => setView('normal')}
                type="button"
                className="mb-3 btn approved_transactions_general_button">
                Back to All Transactions
              </button>
            </div>
          )}
          <div className={classes.pump_table_wrapper}>
            <div className={classes.pump_table}>
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">CIN</th>
                    <th scope="col">Customer Type</th>
                    <th scope="col">Customer Name</th>
                    <th scope="col">Phone No.</th>
                    <th scope="col">Gas Quantity Purchased</th>
                    <th scope="col">Refill Operator</th>
                  </tr>
                </thead>
                {view === 'normal' && (
                  <tbody>
                    {data.message &&
                      data.message?.data.length > 0 &&
                      data.message.data.map((tx) => {
                        const date = new Date(tx.date_filled).toDateString();
                        if (date !== tableDate.current) {
                          tableDate.current = date;
                          return (
                            <React.Fragment key={tx.cin}>
                              <tr className="unapproved_transactions_table_date_text text-end">
                                <td className="p-0" colSpan="7">
                                  {new Date(tableDate.current).toDateString()}
                                </td>
                              </tr>
                              <tr>
                                <td>{tx.cin}</td>
                                <td>{tx.customer_type}</td>
                                <td>
                                  {tx.customer_lastname} {tx.customer_firstname}
                                </td>
                                <td>{tx.phone}</td>
                                <td>{tx.gas_quantity} Kg</td>
                                <td>
                                  {tx.operator_firstname} {tx.operator_lastname}
                                </td>
                              </tr>
                            </React.Fragment>
                          );
                        }
                        return (
                          <tr key={tx.cin}>
                            <td>{tx.cin}</td>
                            <td>{tx.customer_type}</td>
                            <td>
                              {tx.customer_lastname} {tx.customer_firstname}
                            </td>
                            <td>{tx.phone}</td>
                            <td>{tx.gas_quantity} Kg</td>
                            <td>
                              {tx.operator_firstname} {tx.operator_lastname}
                            </td>
                          </tr>
                        );
                      })}
                    {data.message?.data && data.message.data.length === 0 && (
                      <tr>
                        <td colSpan={6}>
                          <div className="d-flex justify-content-center w-100">
                            <p>No Transactions</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                )}
                {view === 'searching' && (
                  <tbody>
                    {searchError && (
                      <tr>
                        <td colSpan={6}>
                          <div className="d-flex justify-content-center w-100">
                            <p className="text-danger">{detectError(searchError)}</p>
                          </div>
                        </td>
                      </tr>
                    )}
                    {searchLoading && (
                      <tr className="py-3">
                        <td colSpan={6}>
                          <div className="d-flex justify-content-center w-100">
                            <Spinner className="spinner" animation="border" variant="dark" />
                          </div>
                        </td>
                      </tr>
                    )}
                    {searchPumps &&
                      searchPumps.length > 0 &&
                      searchPumps.map((tx) => (
                        <tr key={tx.cin}>
                          <td>{tx.cin}</td>
                          <td>{tx.customer_type}</td>
                          <td>
                            {tx.customer_lastname} {tx.customer_firstname}
                          </td>
                          <td>{tx.phone}</td>
                          <td>{tx.gas_quantity} Kg</td>
                          <td>
                            {tx.operator_firstname} {tx.operator_lastname}
                          </td>
                        </tr>
                      ))}
                    {!searchLoading && searchPumps && searchPumps.length === 0 && (
                      <tr>
                        <td colSpan={6}>
                          <div className="d-flex justify-content-center w-100">
                            <p>No Transactions Found</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                )}
                {view === 'filtering' && (
                  <tbody>
                    {filterError && (
                      <tr>
                        <td colSpan={6}>
                          <div className="d-flex justify-content-center w-100">
                            <p className="text-danger">{filterError}</p>
                          </div>
                        </td>
                      </tr>
                    )}
                    {filterLoading && (
                      <tr className="py-3">
                        <td colSpan={6}>
                          <div className="d-flex justify-content-center w-100">
                            <Spinner className="spinner" animation="border" variant="dark" />
                          </div>
                        </td>
                      </tr>
                    )}
                    {!filterLoading &&
                      filterResult &&
                      filterResult.length > 0 &&
                      filterResult.map((tx) => (
                        <tr key={tx.cin}>
                          <td>{tx.cin}</td>
                          <td>{tx.customer_type}</td>
                          <td>
                            {tx.customer_lastname} {tx.customer_firstname}
                          </td>
                          <td>{tx.phone}</td>
                          <td>{tx.gas_quantity} Kg</td>
                          <td>
                            {tx.operator_firstname} {tx.operator_lastname}
                          </td>
                        </tr>
                      ))}
                    {!filterLoading && filterResult && filterResult.length === 0 && (
                      <tr>
                        <td colSpan={6}>
                          <div className="d-flex justify-content-center w-100">
                            <p>No Data Found</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                )}
              </table>
            </div>
          </div>
          {view === 'normal' && (
            <LongPaginationHandler
              elementsPerPage={data?.message?.per_page || 20}
              totalElements={data?.message?.total || 0}
              activePage={page}
              paginate={paginate}
            />
          )}
          {view === 'filtering' && (
            <LongPaginationHandler
              elementsPerPage={filterPerPage}
              totalElements={filterTotal}
              activePage={filterPage}
              paginate={paginateFilter}
            />
          )}
        </div>
      )}
    </>
  );
};

export default PumpTable;
